"use client"
import { VStack } from "@chakra-ui/react/stack"
import { domAnimation, LazyMotion, useScroll } from "framer-motion"
import { usePathname } from "next/navigation"
import React, { useLayoutEffect } from "react"

import Inner from "@/components/Inner"
import ScrollUp from "@/components/ScrollUp"
import { logMessage } from "@/config/siteConfig"

const pageTransition = {
  default: "none",
  "nav-back": "inner-perspective",
  "nav-forward": "inner-perspective",
  "work-filter": "none",
  "work-page-back": "none",
  "work-page-forward": "none",
} as const

// Create a separate component for scroll tracking
const ScrollTracker = () => {
  const { scrollYProgress } = useScroll({
    //layoutEffect: false,
    offset: ["start start", "end end"],
  })

  // Reset scroll progress on mount and after suspense
  React.useEffect(() => {
    // Reset to 0 initially
    scrollYProgress.set(0)

    // Force a re-render after hydration to ensure scroll tracking starts
    const timeoutId = setTimeout(() => {
      // Get current scroll position
      const currentScroll = window.scrollY
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight
      const progress = maxScroll > 0 ? currentScroll / maxScroll : 0

      // Set the actual scroll progress
      scrollYProgress.set(progress)
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [scrollYProgress])

  return <ScrollUp pageProgress={scrollYProgress} />
}

export default function Template({ children }: { children: React.ReactNode }) {
  // console.log(`%c URL: ${process.env.URL} `, 'background: #000; color: #fff');
  const pathname = usePathname()

  // Reset scroll on pathname changes. Next's default scroll handling can keep
  // the current position when the new page is already visible in the viewport.
  useLayoutEffect(() => {
    if (window.location.hash) {
      return
    }

    window.scrollTo(0, 0)
  }, [pathname])

  if (process.env.NODE_ENV === "production") {
    console.info(logMessage)
  }

  return (
    <>
      <LazyMotion features={domAnimation}>
        <VStack className={`main-template`} width={"100%"}>
          <Inner
            default="none"
            enter={pageTransition}
            exit={pageTransition}
            update={pageTransition}
          >
            {children}
          </Inner>
        </VStack>
      </LazyMotion>

      <ScrollTracker />
    </>
  )
}
